import { useToastManager } from "@porsche-design-system/components-react"
import type { InventoryInformationEditView, InventoryInformationView, PricingView } from "@slfinrtl/ui-bff-routes"
import { getInventoryInformationEditViewRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useRef, useState } from "react"
import { useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { AnimatedStage } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBff, useConfig } from "src/hooks"
import { useScrollToStage } from "src/screens/vehicleDetails/common/useScrollToStage/useScrollToStage"
import { useEditActionButton } from "src/screens/vehicleDetails/hooks/useEditActionButton"
import { useVehicleDetailsContextForStage } from "../../../context/VehicleDetailsContext"
import { PricingFormContainer } from "./PricingFormContainer"
import { Pricing } from "./components/Pricing"
import { pricingContainerMessages } from "./pricingContainer.messages"

interface PricingContainerProps {
	setView: (view: InventoryInformationView) => void
	view: PricingView
}

export const PricingContainer = withErrorHandler(({ setView, view }: PricingContainerProps) => {
	const { formatMessage } = useIntl()
	const { inventoryId, mode, notify, setEditStage } = useVehicleDetailsContextForStage("pricing")

	const [editView, setEditView] = useState<InventoryInformationEditView | undefined>()
	const [isEditViewLoading, setIsEditViewLoading] = useState(false)

	const { vatDeductibleAvailable } = useConfig()
	const { addMessage } = useToastManager()

	const fetchEditView = useBff(
		getInventoryInformationEditViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)

	const handleEditClick = useCallback((): Promise<void> => {
		setIsEditViewLoading(true)
		return fetchEditView()
			.then((result) => {
				setEditView(result)
				setEditStage("pricing")
			})
			.finally(() => setIsEditViewLoading(false))
	}, [fetchEditView, setEditStage])

	const ref = useRef(null)
	useScrollToStage({
		callbackFn: handleEditClick,
		editStage: "pricing",
		ref,
	})

	const onExit = useCallback(() => {
		setEditStage(undefined)
	}, [setEditStage])

	const onSaved = useCallback(
		(inventoryInformationView: InventoryInformationView) => {
			setView(inventoryInformationView)
			onExit()
			addMessage({
				state: "success",
				text: formatMessage(commonMessages.saveSuccess),
			})
			notify({
				event: "inventoryInformationUpdated",
				payload: inventoryInformationView,
			})
			notify({
				event: "inventoryInformationViewChanged",
				payload: inventoryInformationView,
			})
		},
		[setView, onExit, addMessage, formatMessage, notify],
	)

	const actionButton = useEditActionButton("pricing", view.pricingEdit, {
		fetch: handleEditClick,
		isLoading: isEditViewLoading,
	})

	return (
		<AnimatedStage ref={ref} title={formatMessage(pricingContainerMessages.headline)} actionButtons={actionButton}>
			<ErrorNotificationInline />
			{mode !== "edit" || !editView ? (
				view && <Pricing view={{ ...view }} usePriceHistory isVatDeductibleAvailable={vatDeductibleAvailable} />
			) : (
				<PricingFormContainer inventoryId={inventoryId} view={editView} onCancel={onExit} onSaved={onSaved} />
			)}
		</AnimatedStage>
	)
})
